import { Component, OnDestroy, OnInit } from '@angular/core';
import { GetTicketListRequest, GetTicketListResponse_Outcome, SlimTicket, TicketState } from '@proto/hubspotr';
import { TicketRequest } from '@proto/mypascom';
import { firstValueFrom } from 'rxjs';
import { Model } from 'src/app/app.model';
import { Subscription } from 'src/app/subscription/subscription.model';
import { SupportV2Service } from '../supportv2.service';
import { AutorunComponent } from '@myshared/autorun.component';
import { SubscriptionService } from 'src/app/subscription/subscription.service';
import { ToastService } from 'src/app/toast.service';
import { I18NextService } from 'angular-i18next';
import { CloudStatusType, SupportService } from 'src/app/support/support.service';
import { Ticket, TicketState as V1TicketState } from 'src/app/support/support.model';

export type UnifiedTicketItem =
  | { source: 'v1'; ticket: Ticket; sortDate: number }
  | { source: 'v2'; ticket: SlimTicket; sortDate: number };

@Component({
  selector: 'app-ticket-list-v2',
  templateUrl: './ticket-list-v2.component.html',
  standalone: false
})
export class TicketListV2Component extends AutorunComponent implements OnInit, OnDestroy {

  public subscriptions: Subscription[] = [];
  public currentTicketsToDisplay: UnifiedTicketItem[] = [];

  private v2Tickets: SlimTicket[] = [];
  private v1Tickets: Ticket[] = [];
  private showClosed = false;
  private searchQuery = '';
  private v1Page = 1;
  private searchTimeout: any;

  public cloudStatus: CloudStatusType = '';
  public timeToShowDialog: Boolean = false;

  constructor(private m: Model,
    private supportV2Service: SupportV2Service,
    private supportV1Service: SupportService,
    private subscriptionService: SubscriptionService,
    private toastService: ToastService,
    private i18Next: I18NextService) {
    super();
  }

  ngOnInit(): void {
    this.getCloudStatus();
    this.subscriptionService.refresh();
    this.autorun(() => {
      this.subscriptions = this.m.subscription.subscriptions;
    });
    this.loadTickets(true);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  getCloudStatus() {
    this.supportV1Service.getCloudStatus().subscribe(status => {
      this.cloudStatus = status;
      this.canShowStatusDialog();
    });
  }

  get currentCloudStatus(): CloudStatusType {
    return this.cloudStatus;
  }

  canShowStatusDialog() {
    if (this.cloudStatus && this.cloudStatus !== 'operational') {
      if (this.supportV1Service.canExecuteDailyAction('dailyAction')) {
        this.timeToShowDialog = true;
      }
    }
  }

  get showStatusDialog() {
    return this.timeToShowDialog;
  }

  async loadTickets(reset: boolean) {
    if (reset) {
      this.v1Page = 1;
      this.v1Tickets = [];
      this.v2Tickets = [];
    } else {
      this.v1Page++;
    }

    const tickets: Promise<void>[] = [this.fetchV1Tickets(reset)];
    if (reset) {
      tickets.push(this.fetchV2Tickets());
    }
    await Promise.all(tickets);
    this.buildDisplayList();
  }

  nextPage() {
    this.loadTickets(false);
  }

  get needsPagination(): boolean {
    return this.v1Tickets.length >= (this.v1Page * 10);
  }

  private async fetchV2Tickets() {
    const getTicketListRequest = GetTicketListRequest.create({
      applianceIds: [],
      includeTicketStates: this.showClosed
        ? [TicketState.STATE_CLOSED]
        : [TicketState.STATE_UNKNOWN, TicketState.STATE_NEW, TicketState.STATE_WAIT_ON_CONTACT, TicketState.STATE_WAIT_ON_US, TicketState.STATE_WAIT_ON_DEV, TicketState.STATE_WAIT_ON_APPOINTMENT, TicketState.STATE_WAIT_ON_CLOSE, TicketState.UNRECOGNIZED]
    });
    const getTicketReq = TicketRequest.create({
      request: {
        $case: 'getTicketListRequest',
        getTicketListRequest: getTicketListRequest
      }
    });

    try {
      const response = await firstValueFrom(this.supportV2Service.getAllTickets(getTicketReq));
      if (response.outcome === GetTicketListResponse_Outcome.OUTCOME_SUCCESS) {
        this.v2Tickets = response.slimTickets;
      } else {
        this.toastService.error(this.i18Next.t('support_ticket_get_list_error'), 'error');
      }
    } catch {
      this.toastService.error(this.i18Next.t('support_ticket_get_list_error'), 'error');
    }
  }

  private async fetchV1Tickets(reset: boolean) {
    const stateFilter = this.showClosed
      ? [V1TicketState.closed]
      : [V1TicketState.new, V1TicketState.open, V1TicketState.pendingReminder, V1TicketState.pendingClose];

    try {
      const tickets = await firstValueFrom(this.supportV1Service.getTickets$(stateFilter, this.v1Page, this.searchQuery));
      if (reset) {
        this.v1Tickets = tickets;
      } else {
        this.v1Tickets = [...this.v1Tickets, ...tickets];
      }
    } catch (e) {
      if (reset) {
        this.v1Tickets = [];
      } else {
        this.v1Page--;
        console.error('Failed to load more v1 tickets', e);
      }
    }
  }

  private buildDisplayList() {
    const v1Items: UnifiedTicketItem[] = this.v1Tickets.map(t => ({
      source: 'v1' as const,
      ticket: t,
      sortDate: new Date(t.createdAt).getTime()
    }));
    const v2Items: UnifiedTicketItem[] = this.v2Tickets.map(t => ({
      source: 'v2' as const,
      ticket: t,
      sortDate: t.createdAt * 1000 // proto field is epoch seconds
    }));

    let merged = [...v2Items, ...v1Items].sort((a, b) => b.sortDate - a.sortDate);

    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      merged = merged.filter(item => {
        if (item.source === 'v2') {
          return item.ticket.title.toLowerCase().includes(query) ||
            item.ticket.applianceName.toLowerCase().includes(query) ||
            item.ticket.ticketId.includes(query);
        } else {
          return item.ticket.title.toLowerCase().includes(query) ||
            item.ticket.appliance.toLowerCase().includes(query) ||
            item.ticket.number.includes(query);
        }
      });
    }

    this.currentTicketsToDisplay = merged;
  }

  doFilter(event: string) {
    this.searchQuery = event;
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(() => {
      this.loadTickets(true);
    }, 500);
  }

  showClosedTickets(value: boolean) {
    this.showClosed = value;
    this.loadTickets(true);
  }

  getSubscriptionName(applianceId: string): string {
    return this.subscriptions.find((sub: Subscription) => +sub.appliance_id === +applianceId)?.name ?? '';
  }
}
